import useLocalStorage from 'beautiful-react-hooks/useLocalStorage'
import React, { createContext, useContext, useEffect } from 'react'
import { useFeatureFlags } from '@opallabs/api'
import { WorkspaceContext } from '@opallabs/hooks'
import { AUTO_COLOR_BY, staticSorts } from '../sortHelpers'
import {
  ContentNamePriority,
  CoverImageDisplayMode,
  LocalStorageKeys,
  PlaceholderColor,
  WeekViewMode,
  WeekViewSort,
  WeekViewSortType,
} from '../types'

type WeekViewSettingsContextValue = {
  collapseContent: boolean
  colorBy: WeekViewSort
  coverImageDisplay: CoverImageDisplayMode
  namePriority: ContentNamePriority
  placeholderColor: PlaceholderColor
  selectedSort: WeekViewSort
  spanInMarketDays: boolean
  viewMode: WeekViewMode
  setCollapsedContent: React.Dispatch<React.SetStateAction<boolean>>
  setColorBy: React.Dispatch<React.SetStateAction<WeekViewSort>>
  setCoverImageDisplay: React.Dispatch<
    React.SetStateAction<CoverImageDisplayMode>
  >
  setNamePriority: React.Dispatch<React.SetStateAction<ContentNamePriority>>
  setPlaceholderColor: React.Dispatch<React.SetStateAction<PlaceholderColor>>
  setSelectedSort: React.Dispatch<React.SetStateAction<WeekViewSort>>
  setSpanInMarketDays: React.Dispatch<React.SetStateAction<boolean>>
  setValidSorts: React.Dispatch<React.SetStateAction<WeekViewSort[]>>
  setViewMode: React.Dispatch<React.SetStateAction<WeekViewMode>>
  workflowOnMomentsEnabled: boolean
}

export const WeekViewSettingsContext = createContext<
  WeekViewSettingsContextValue | undefined
>(undefined)
WeekViewSettingsContext.displayName = 'WeekViewSettingsContext'

const defaultSort: WeekViewSort = {
  id: 'day_segment',
  title: 'Time of Day',
  type: WeekViewSortType.Time,
}

export const WeekViewSettingsProvider = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  const workspace = useContext(WorkspaceContext)
  const workspaceKey = workspace?.uuid ?? 'default'
  const { data: featureFlags } = useFeatureFlags()

  const [coverImageDisplay, setCoverImageDisplay] =
    useLocalStorage<CoverImageDisplayMode>(
      LocalStorageKeys.CoverImageDisplayMode,
      CoverImageDisplayMode.Scaled
    )

  const [collapseContent, setCollapsedContent] = useLocalStorage<boolean>(
    LocalStorageKeys.CollapseContent,
    false
  )

  /**
   * Only `selectedSort` needs workspace scoping: it references a label-set ID,
   * and label sets are the only week-view preference whose meaning is
   * workspace-specific (cover image / collapse / placeholder color are purely
   * visual and shared across workspaces). Different workspaces often have
   * label sets with the same title but different IDs, so without scoping, the
   * selected-sort from workspace A would leak into workspace B and silently
   * filter moments by a non-matching ID (bug #16898). The caller passes
   * `key={workspace.uuid}` on this provider so React remounts it on workspace
   * change — that remount is what resets `validSorts` and triggers
   * `useLocalStorage` to re-read under the new scoped key (it only reads its
   * key on first render).
   */
  const [selectedSort, setSelectedSort] = useLocalStorage<WeekViewSort>(
    `${LocalStorageKeys.SelectedSort}:${workspaceKey}`,
    defaultSort
  )

  // Workspace-scoped for the same reason as `selectedSort` — it can reference
  // a label-set ID, which differs per workspace (bug #16898).
  const [colorBy, setColorBy] = useLocalStorage<WeekViewSort>(
    `${LocalStorageKeys.ColorBy}:${workspaceKey}`,
    AUTO_COLOR_BY
  )

  // Purely visual, so global (not workspace-scoped) like the other display
  // preferences.
  const [namePriority, setNamePriority] = useLocalStorage<ContentNamePriority>(
    LocalStorageKeys.ContentNamePriority,
    ContentNamePriority.Content
  )

  const [placeholderColor, setPlaceholderColor] =
    useLocalStorage<PlaceholderColor>(
      LocalStorageKeys.PlaceholderColor,
      PlaceholderColor.yellow
    )

  const [viewMode, setViewMode] = useLocalStorage<WeekViewMode>(
    LocalStorageKeys.ViewMode,
    WeekViewMode.Moment
  )

  const [spanInMarketDays, setSpanInMarketDays] = useLocalStorage<boolean>(
    LocalStorageKeys.SpanInMarketDays,
    true
  )

  const [validSorts, setValidSorts] =
    React.useState<WeekViewSort[]>(staticSorts)

  /**
   * Week View's sort options only list Label Sets that are applied to Moments
   * in the current week. Because of this, the user can end up in a situation where
   * an invalid WeekViewSort is pulled from localStorage.
   * As the LabelSets for a week are pulled in, we will update `validSorts` and cause this
   * effect to run. If the selectedSort is not found in the validSorts array, then
   * we will fall back to Time sorting.
   */
  useEffect(() => {
    if (selectedSort?.type === WeekViewSortType.LabelSet) {
      // Skip validation if label sets haven't loaded yet (validSorts only contains
      // the 2 static sorts: Time of Day and Channel). This prevents resetting the
      // user's LabelSet preference during the initial load race condition.
      const hasOnlyStaticSorts = validSorts.length === staticSorts.length

      if (hasOnlyStaticSorts) {
        return
      }

      const sortIsValid = validSorts.some(
        (sort) =>
          selectedSort.type === sort.type &&
          String(selectedSort.id) === String(sort.id)
      )

      if (!sortIsValid) setSelectedSort(defaultSort)
    }
  }, [selectedSort, setSelectedSort, validSorts])

  return (
    <WeekViewSettingsContext.Provider
      value={{
        collapseContent: collapseContent ?? false,
        colorBy: colorBy ?? AUTO_COLOR_BY,
        coverImageDisplay: coverImageDisplay ?? CoverImageDisplayMode.Scaled,
        namePriority: namePriority ?? ContentNamePriority.Content,
        placeholderColor: placeholderColor ?? PlaceholderColor.yellow,
        setColorBy,
        setNamePriority,
        setPlaceholderColor,
        selectedSort: selectedSort ?? defaultSort,
        spanInMarketDays: spanInMarketDays ?? true,
        viewMode: viewMode ?? WeekViewMode.Moment,
        setCollapsedContent,
        setCoverImageDisplay,
        setSelectedSort,
        setSpanInMarketDays,
        setValidSorts,
        setViewMode,
        workflowOnMomentsEnabled: featureFlags?.workflow_on_moments || false,
      }}
    >
      {children}
    </WeekViewSettingsContext.Provider>
  )
}

export const useWeekViewSettings = (): WeekViewSettingsContextValue => {
  const context = useContext(WeekViewSettingsContext)

  if (context === undefined) {
    throw new Error(
      'useWeekViewSettings must be used within a WeekViewSettingsContext'
    )
  }

  return context
}
